/* Cart context for managing shopping cart state across the application
   Provides cart items, add/remove/update functions, and cart totals
   Uses localStorage for persistence and generates unique session IDs
   Updated: Added totalPalletPlaces and exceedsPalletLimit (max 15) for capacity checking
   Updated: Added plants_per_box and boxes_per_pallet_position fields to CartItem
   Updated: Added category_slug to CartItem for category-based delivery fee calculation
   Security update: the cart session id now comes from the shared helper that is also
   sent as a request header, so the database only returns this browser's cart. */


import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, getCartSessionId } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import { trackGoal } from '@/lib/datafast';
import { toast } from 'sonner';

export interface CartItem {
  id: string;
  product_variant_id?: string | null;
  product_id?: string | null;
  product_name: string;
  variant_size: string;
  variant_type?: 'Kluit' | 'Pot' | 'Kale wortel';
  quantity: number;
  unit_price: number;
  original_price?: number;
  image_url?: string;
  slug?: string;
  is_simple_product?: boolean;
  supplier?: 'Kwekerij' | 'Overig';
  transport_method?: 'kuubkist' | 'halve_kuubkist' | 'pallet' | 'tuinplanten' | 'none';
  category_slug?: string;
  btw_percentage?: number;
  minimum_order_quantity?: number;
  plants_per_box?: number | null;
  boxes_per_pallet_position?: number | null;
}

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  total: number;
  totalDiscount: number;
  isLoading: boolean;
  hasMultipleSuppliers: boolean;
  totalPalletPlaces: number;
  exceedsPalletLimit: boolean;
  suppliers: Set<string>;
  addItem: (item: Omit<CartItem, 'id' | 'quantity'> & { quantity?: number }) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function generateSessionId(): string {
  return getCartSessionId();
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState<string>('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setSessionId(generateSessionId());
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      loadCart();
    }
  }, [sessionId]);

  const loadCart = async () => {
    try {
      setIsLoading(true);

      const { data: cartItems, error } = await supabase
        .from('cart_items')
        .select(`
          id,
          product_variant_id,
          product_id,
          quantity,
          product_variants (
            id,
            size,
            type,
            price_tier_1,
            price_tier_2,
            price_tier_3,
            price_tier_4,
            slug,
            supplier,
            transport_method,
            minimum_order_quantity,
            plants_per_box,
            boxes_per_pallet_position,
            products (
              id,
              name,
              base_slug,
              image_url,
              btw_percentage,
              product_categories (
                slug
              )
            )
          ),
          products (
            id,
            name,
            base_slug,
            image_url,
            btw_percentage,
            simple_price,
            is_simple_product,
            supplier,
            transport_method,
            product_categories (
              slug
            )
          )
        `)
        .eq('session_id', sessionId);

      if (error) throw error;

      const formattedItems: CartItem[] = (cartItems || []).map((item: any) => {
        const quantity = item.quantity;

        // Check if this is a simple product or a variant product
        if (item.product_id && item.products) {
          // Simple product
          const product = item.products;
          const priceInclBTW = calculatePriceInclBTW(product.simple_price, product.btw_percentage || 9);

          return {
            id: item.id,
            product_id: item.product_id,
            product_variant_id: null,
            product_name: product.name || 'Unknown Product',
            variant_size: '',
            quantity: item.quantity,
            unit_price: priceInclBTW,
            original_price: priceInclBTW,
            slug: product.base_slug,
            image_url: product.image_url,
            is_simple_product: true,
            supplier: product.supplier || 'Kwekerij',
            transport_method: product.transport_method || 'none',
            category_slug: product.product_categories?.slug || '',
            btw_percentage: product.btw_percentage || 9,
          };
        } else if (item.product_variant_id && item.product_variants) {
          // Variant product with graduated pricing (per-item quantity, not cumulative)
          const priceTier1 = parseFloat(item.product_variants?.price_tier_1 || '0');
          const priceTier2 = item.product_variants?.price_tier_2 ? parseFloat(item.product_variants.price_tier_2) : null;
          const priceTier3 = item.product_variants?.price_tier_3 ? parseFloat(item.product_variants.price_tier_3) : null;
          const priceTier4 = item.product_variants?.price_tier_4 ? parseFloat(item.product_variants.price_tier_4) : null;
          const btwPercentage = item.product_variants?.products?.btw_percentage || 9;
          const isKaleWortel = item.product_variants?.type === 'Kale wortel';

          // Determine the correct price tier based on THIS item's quantity only
          let priceExclBTW: number;
          if (isKaleWortel) {
            // Kale wortel pricing: 100-199, 200-299, 300-499, 500+
            if (quantity >= 500 && priceTier4) {
              priceExclBTW = priceTier4;
            } else if (quantity >= 300 && priceTier3) {
              priceExclBTW = priceTier3;
            } else if (quantity >= 200 && priceTier2) {
              priceExclBTW = priceTier2;
            } else if (quantity >= 100 && priceTier1) {
              priceExclBTW = priceTier1;
            } else {
              priceExclBTW = parseFloat(item.unit_price as any);
            }
          } else {
            // Standard pricing: 25-50, 51-75, 76-100, 101+
            if (quantity >= 101 && priceTier4) {
              priceExclBTW = priceTier4;
            } else if (quantity >= 76 && priceTier3) {
              priceExclBTW = priceTier3;
            } else if (quantity >= 51 && priceTier2) {
              priceExclBTW = priceTier2;
            } else {
              priceExclBTW = priceTier1;
            }
          }

          const priceInclBTW = calculatePriceInclBTW(priceExclBTW, btwPercentage);
          const originalPriceInclBTW = calculatePriceInclBTW(priceTier1, btwPercentage);

          return {
            id: item.id,
            product_variant_id: item.product_variant_id,
            product_id: null,
            product_name: item.product_variants?.products?.name || 'Unknown Product',
            variant_size: item.product_variants?.size || '',
            variant_type: item.product_variants?.type as 'Kluit' | 'Pot' | 'Kale wortel' | undefined,
            quantity: item.quantity,
            unit_price: priceInclBTW,
            original_price: originalPriceInclBTW,
            slug: item.product_variants?.products?.base_slug,
            image_url: item.product_variants?.products?.image_url,
            is_simple_product: false,
            supplier: item.product_variants?.supplier || 'Kwekerij',
            transport_method: item.product_variants?.transport_method || 'none',
            category_slug: item.product_variants?.products?.product_categories?.slug || '',
            btw_percentage: btwPercentage,
            minimum_order_quantity: item.product_variants?.minimum_order_quantity,
            plants_per_box: item.product_variants?.plants_per_box,
            boxes_per_pallet_position: item.product_variants?.boxes_per_pallet_position,
          };
        }

        // Fallback for invalid items
        return {
          id: item.id,
          product_variant_id: null,
          product_id: null,
          product_name: 'Unknown Product',
          variant_size: '',
          quantity: item.quantity,
          unit_price: 0,
          is_simple_product: false,
        };
      });

      // Resolve primary images from product_images table
      const productIds = [...new Set(formattedItems.map(i => i.product_id || '').filter(Boolean))];
      const variantProductIds = [...new Set((cartItems || [])
        .filter((i: any) => i.product_variants?.products?.id)
        .map((i: any) => i.product_variants.products.id))];
      const allProductIds = [...new Set([...productIds, ...variantProductIds])];

      if (allProductIds.length > 0) {
        const { data: primaryImages } = await supabase
          .from('product_images')
          .select('product_id, storage_path, uses_new_bucket')
          .in('product_id', allProductIds)
          .is('variant_id', null)
          .eq('is_primary', true);

        if (primaryImages && primaryImages.length > 0) {
          const imageMap = new Map(primaryImages.map(img => [
            img.product_id,
            getProductImageUrl(img.storage_path, img.uses_new_bucket, 'thumb')
          ]));

          for (const item of formattedItems) {
            const pid = item.product_id || (cartItems || []).find(
              (ci: any) => ci.id === item.id
            )?.product_variants?.products?.id;
            if (pid && imageMap.has(pid)) {
              item.image_url = imageMap.get(pid);
            }
          }
        }
      }

      setItems(formattedItems);
    } catch (error) {
      // Error loading cart
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = async (item: Omit<CartItem, 'id' | 'quantity'> & { quantity?: number }) => {
    try {
      const quantity = item.quantity || 1;

      // Check if item already exists in cart
      // For simple products, check by product_id; for variants, check by product_variant_id
      const { data: existing } = await supabase
        .from('cart_items')
        .select('id, quantity')
        .eq('session_id', sessionId)
        .match(
          item.product_id
            ? { product_id: item.product_id }
            : { product_variant_id: item.product_variant_id }
        )
        .maybeSingle();

      if (existing) {
        // Update quantity
        const { error } = await supabase
          .from('cart_items')
          .update({ quantity: existing.quantity + quantity })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Insert new item
        const insertData: any = {
          session_id: sessionId,
          quantity,
        };

        // Add either product_id or product_variant_id
        if (item.product_id) {
          insertData.product_id = item.product_id;
        } else {
          insertData.product_variant_id = item.product_variant_id;
        }

        const { error } = await supabase
          .from('cart_items')
          .insert(insertData);

        if (error) {
          throw error;
        }
      }

      toast.success(item.product_name, {
        description: `${quantity}x toegevoegd`,
        icon: item.image_url ? (
          <img
            src={item.image_url}
            alt={item.product_name}
            className="w-14 h-14 object-cover rounded-md"
          />
        ) : undefined,
      });

      trackGoal('add_to_cart', {
        product_name: item.product_name,
        variant_size: item.variant_size,
        quantity,
        unit_price: item.unit_price,
      });

      await loadCart();
    } catch (error) {
      toast.error('Fout bij toevoegen aan winkelwagen', {
        description: 'Probeer het opnieuw',
      });
    }
  };

  const removeItem = async (itemId: string) => {
    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('session_id', sessionId)
        .eq('id', itemId);

      if (error) throw error;
      await loadCart();
    } catch (error) {
      // Error removing item from cart
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    try {
      if (quantity <= 0) {
        await removeItem(itemId);
        return;
      }

      const currentItem = items.find((i) => i.id === itemId);
      const minQty = currentItem?.minimum_order_quantity || (currentItem?.is_simple_product ? 1 : 25);
      const clampedQty = Math.max(minQty, Math.round(quantity));

      if (clampedQty === currentItem?.quantity) return;

      const { error } = await supabase
        .from('cart_items')
        .update({ quantity: clampedQty })
        .eq('session_id', sessionId)
        .eq('id', itemId);

      if (error) throw error;
      await loadCart();
    } catch (error) {
      // Error updating quantity
    }
  };

  const clearCart = async () => {
    try {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('session_id', sessionId);

      if (error) throw error;
      setItems([]);
    } catch (error) {
      // Error clearing cart
    }
  };

  const refreshCart = async () => {
    await loadCart();
  };

  // All prices already include BTW
  const total = items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate total discount from graduated pricing (staffelkorting)
  const totalDiscount = items.reduce((sum, item) => {
    if (item.original_price && item.original_price > item.unit_price) {
      return sum + (item.original_price - item.unit_price) * item.quantity;
    }
    return sum;
  }, 0);

  // Detect multiple suppliers in cart
  const suppliers = new Set(
    items
      .filter(item => item.supplier) // Only include items with supplier info
      .map(item => item.supplier as string)
  );
  const hasMultipleSuppliers = suppliers.size > 1;

  const MAX_PALLET_PLACES = 15;
  const totalPalletPlaces = items.reduce((total, item) => {
    if (item.is_simple_product || !item.plants_per_box || !item.boxes_per_pallet_position) return total;
    const boxesNeeded = Math.ceil(item.quantity / item.plants_per_box);
    const palletPlaces = Math.ceil(boxesNeeded / item.boxes_per_pallet_position);
    return total + palletPlaces;
  }, 0);
  const exceedsPalletLimit = totalPalletPlaces > MAX_PALLET_PLACES;

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        total,
        totalDiscount,
        isLoading,
        hasMultipleSuppliers,
        totalPalletPlaces,
        exceedsPalletLimit,
        suppliers,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
